import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CollectionColumnDomain } from '@shared/types';
import { Icon } from './icons';
import type { CollectionTableColumn } from './collection-table';
import { EditorTabs } from './primitives';

export interface CollectionColumnConfig extends CollectionTableColumn {
  locked?: boolean;
  defaultVisible?: boolean;
}

export interface CollectionColumnSettingsStrings {
  title: string;
  description: string;
  reset: string;
  close: string;
  locked: string;
  columnsTab?: string;
}

export interface CollectionColumnSettingsExtraTab {
  id: 'importExport';
  label: string;
  content: ReactNode;
}

export function getDefaultVisibleColumnIds(columns: CollectionColumnConfig[]): string[] {
  return columns.filter((column) => column.locked || column.defaultVisible !== false).map((column) => column.id);
}

export function resolveVisibleCollectionColumns(
  columns: CollectionColumnConfig[],
  visibleColumnIds: string[] | undefined
): CollectionColumnConfig[] {
  const visibleIds = new Set(visibleColumnIds && visibleColumnIds.length > 0 ? visibleColumnIds : getDefaultVisibleColumnIds(columns));

  return columns.filter((column) => column.locked || visibleIds.has(column.id));
}

export function toggleVisibleColumnId(columns: CollectionColumnConfig[], visibleColumnIds: string[], columnId: string): string[] {
  const column = columns.find((item) => item.id === columnId);

  if (!column || column.locked) {
    return visibleColumnIds;
  }

  const visibleSet = new Set(visibleColumnIds.length > 0 ? visibleColumnIds : getDefaultVisibleColumnIds(columns));

  if (visibleSet.has(columnId)) {
    visibleSet.delete(columnId);
  } else {
    visibleSet.add(columnId);
  }

  for (const lockedColumn of columns.filter((item) => item.locked)) {
    visibleSet.add(lockedColumn.id);
  }

  return columns.filter((item) => visibleSet.has(item.id)).map((item) => item.id);
}

export function CollectionColumnSettingsModal({
  columns,
  domain,
  isOpen,
  strings,
  extraTab,
  visibleColumnIds,
  onClose,
  onReset,
  onVisibleColumnIdsChange
}: {
  columns: CollectionColumnConfig[];
  domain: CollectionColumnDomain;
  isOpen: boolean;
  strings: CollectionColumnSettingsStrings;
  extraTab?: CollectionColumnSettingsExtraTab | undefined;
  visibleColumnIds: string[] | undefined;
  onClose: () => void;
  onReset: () => void;
  onVisibleColumnIdsChange: (columnIds: string[]) => void;
}): JSX.Element | null {
  const resolvedVisibleColumnIds = useMemo(
    () => (visibleColumnIds && visibleColumnIds.length > 0 ? visibleColumnIds : getDefaultVisibleColumnIds(columns)),
    [columns, visibleColumnIds]
  );
  const visibleSet = useMemo(() => new Set(resolvedVisibleColumnIds), [resolvedVisibleColumnIds]);
  const [activeTab, setActiveTab] = useState<'columns' | 'importExport'>('columns');

  useEffect(() => {
    if (isOpen) {
      setActiveTab('columns');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="av-column-settings-modal__overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        aria-labelledby={`${domain}-column-settings-title`}
        aria-modal="true"
        className={['av-column-settings-modal', extraTab ? 'av-column-settings-modal--wide' : ''].filter(Boolean).join(' ')}
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="av-column-settings-modal__header">
          <div>
            <h2 id={`${domain}-column-settings-title`}>{strings.title}</h2>
            <p>{strings.description}</p>
          </div>
          <button className="av-column-settings-modal__close" type="button" onClick={onClose}>
            <Icon name="times" />
          </button>
        </div>

        {extraTab ? (
          <div className="av-column-settings-modal__tabs">
            <EditorTabs
              tabs={[
                { id: 'columns', label: strings.columnsTab ?? strings.title, icon: 'list' },
                { id: extraTab.id, label: extraTab.label, icon: 'document' }
              ]}
              value={activeTab}
              onChange={setActiveTab}
            />
          </div>
        ) : null}

        {activeTab === 'columns' ? (
          <div className="av-column-settings-modal__list">
            {columns.map((column) => {
              const checked = column.locked || visibleSet.has(column.id);

              return (
                <label key={column.id} className="av-column-settings-modal__row">
                  <input
                    checked={checked}
                    disabled={column.locked}
                    type="checkbox"
                    onChange={() => onVisibleColumnIdsChange(toggleVisibleColumnId(columns, resolvedVisibleColumnIds, column.id))}
                  />
                  <span>{column.label}</span>
                  {column.locked ? <em>{strings.locked}</em> : null}
                </label>
              );
            })}
          </div>
        ) : (
          <div className="av-column-settings-modal__content">{extraTab?.content}</div>
        )}

        <div className="av-column-settings-modal__footer">
          {activeTab === 'columns' ? (
            <button className="av-secondary-button" type="button" onClick={onReset}>
              {strings.reset}
            </button>
          ) : null}
          <button className="av-primary-button" type="button" onClick={onClose}>
            {strings.close}
          </button>
        </div>
      </div>
    </div>
  );
}
